import React from 'react';
import classNames from 'classnames';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import Badges from 'buyside/components/presentation/Badges/badges';
import EmployerAssociation from 'buyside/components/presentation/EmployerAssociation/employerAssociation';
import FollowButton, {
  FollowableType
} from 'buyside/components/presentation/FollowButton/followButton';
import Link from 'buyside/components/presentation/Link/link';
import MessageButton from 'buyside/components/presentation/MessageModal/messageButton';
import ProfileImage from 'buyside/components/presentation/ProfileImage/profileImage';
import { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';

import { userUrl, userIdeaUrl, defaultUserImageUrl } from 'buyside/util/urlHelper';
import { useIsMobile } from 'buyside/util/hooks/useIsMobile';

import Envelope from 'SVGs/envelope.svg';

import { MembersIndexCardFragment } from 'interfaces/graphql-typings';

import styles from './membersIndexCardStyles.scss';

const MembersIndexCard: React.FC<{
  canViewUserDetails: boolean;
  membersIndexCardStyle?: string;
  node: MembersIndexCardFragment;
}> = ({ canViewUserDetails, membersIndexCardStyle, node }) => {
  const isMobile = useIsMobile();
  const buttonSize = isMobile ? BUTTON_SIZES.TINY : BUTTON_SIZES.SMALL;

  const { city, imageProfileUrl, summary, user } = node;

  if (!user) return null;

  const { id: userId, displayName, publishedIdeasCount, userBadges, mostRecentEmployer } = user;

  return (
    <div className={classNames(styles.membersIndexCard, membersIndexCardStyle)}>
      <ProfileImage
        className={styles.profileImage}
        size="small"
        source={canViewUserDetails ? imageProfileUrl : defaultUserImageUrl()}
      />
      <div className={styles.mainContentContainer}>
        <div className={styles.userInformationContainer}>
          <div>
            {canViewUserDetails ? (
              <Link className={styles.userDisplayName} to={userUrl(userId)}>
                {displayName}
              </Link>
            ) : (
              <p className={styles.userDisplayName}>
                <AnonymousUserDisplay anonUser={{ title: mostRecentEmployer?.title }} />
              </p>
            )}
            {mostRecentEmployer ? (
              canViewUserDetails ? (
                <EmployerAssociation mostRecentEmployer={mostRecentEmployer} />
              ) : (
                <AnonEmployerAssociation
                  aumRange={mostRecentEmployer.employer?.aumRange}
                  fundType={mostRecentEmployer.employer?.fundType}
                />
              )
            ) : null}
            <p className={styles.profileCity}>{city}</p>
          </div>
          {publishedIdeasCount ? (
            <Link to={userIdeaUrl(userId)}>
              {publishedIdeasCount} Idea{publishedIdeasCount === 1 ? null : 's'}
            </Link>
          ) : null}
        </div>
        {summary && canViewUserDetails && <p className={styles.summary}>{summary}</p>}
        {(userBadges?.edges?.length || 0) > 0 && <Badges userBadges={userBadges?.edges} inline />}
        <div className={styles.actions}>
          <FollowButton
            buttonDisplay="User"
            id={userId}
            size={buttonSize}
            type={FollowableType.USER}
          />
          <MessageButton
            buttonOptions={{
              icon: Envelope,
              size: buttonSize,
              text: 'Send Message',
              type: BUTTON_TYPES.SECONDARY
            }}
            isCurrentEmployer={Boolean(mostRecentEmployer?.currentEmployer)}
            receiverEmployerName={mostRecentEmployer?.employer?.name}
            receiverId={userId}
            receiverName={displayName}
          />
        </div>
      </div>
    </div>
  );
};

export default MembersIndexCard;
